'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { Close, Filter } from '@repo/ui/icons'
import { forwardRef, memo } from 'react'

interface TriggerButtonProps {
  selectedItemLabels: string[]
  clear: () => void
  clearTooltipText: string
  disabled: boolean
  /** Label shown when no item is selected (v5 parity, defaults to "All") */
  allLabel?: string
}

const TriggerButton = forwardRef<HTMLButtonElement, TriggerButtonProps>(
  ({ selectedItemLabels, clear, clearTooltipText, disabled, allLabel, ...props }, forwardRef) => {
    const [first, second, ...rest] = selectedItemLabels
    const triggerLabel =
      selectedItemLabels.length === 0
        ? (allLabel ?? 'All')
        : first +
          (second ? `, ${second}` : '') +
          (rest.length > 0 ? `, and ${rest.length} more` : '')

    function handleXClick(event: React.MouseEvent<HTMLButtonElement>) {
      // stopPropagation to avoid triggering the popover open/close when clearing
      event.stopPropagation()
      clear()
    }

    return (
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        ref={forwardRef}
        data-testid={`filter-trigger-${(allLabel ?? 'all').toLowerCase().replace(/\s+/g, '-')}`}
        className={cn(
          'flex items-center gap-[2px]',
          'py-[3px] pl-[5px]',
          selectedItemLabels.length > 0 ? 'pr-[5px]' : 'pr-[10px]'
        )}
        {...props}
      >
        <Icon icon={<Filter />} className="text-secondary-foreground" />
        <span
          className={cn(
            'text-3xs text-foreground',
            'max-md:max-w-[180px] max-md:overflow-hidden max-md:text-ellipsis max-md:whitespace-nowrap'
          )}
        >
          {triggerLabel}
        </span>
        {selectedItemLabels.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                // oxlint-disable-next-line typescript/consistent-type-assertions
                onClick={handleXClick as unknown as React.MouseEventHandler}
                role="button"
                id="x-icon"
                className={cn(
                  'cursor-pointer border-none bg-transparent p-0',
                  'text-secondary-foreground hover:text-foreground'
                )}
              >
                <Icon icon={<Close />} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{clearTooltipText}</TooltipContent>
          </Tooltip>
        )}
      </Button>
    )
  }
)

TriggerButton.displayName = 'TriggerButton'

export default memo(TriggerButton)
